import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";

interface SocialAccountLite {
  id: string;
  platform: string;
  handle: string;
  display_name: string | null;
  status: string;
}

interface Config {
  id: string;
  name: string;
  social_account_id: string;
  account_handle: string | null;
  account_display_name: string | null;
  platform: string | null;
  status: "active" | "paused";
  min_likes: number;
  min_age_days: number;
  max_age_days: number;
  cadence_days: number;
  next_due_at: number;
  last_run_at: number | null;
  last_error: string | null;
  success_count: number;
  failed_count: number;
  created_at: number;
  updated_at: number;
}

interface RunRow {
  id: string;
  source_external_post_id: string | null;
  source_likes_snapshot: number | null;
  source_published_at: number | null;
  source_content_preview: string | null;
  retweet_external_post_id: string | null;
  status: string;
  error_message: string | null;
  created_at: number;
}

interface PreviewCandidate {
  external_post_id: string;
  external_post_url: string | null;
  content: string;
  likes: number;
  published_at: number;
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function fmtRelative(ms: number): string {
  const diff = Date.now() - ms;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 1) return "today";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function AutoRetweetPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState<SocialAccountLite[]>([]);
  const [configs, setConfigs] = useState<Config[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Config | null>(null);
  const [showRunsFor, setShowRunsFor] = useState<Config | null>(null);
  const [showPreviewFor, setShowPreviewFor] = useState<Config | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get<{ social_accounts: SocialAccountLite[] }>("/api/social-accounts"),
        api.get<{ configs: Config[] }>("/api/auto-retweet"),
      ]);
      setAccounts(
        (a.social_accounts ?? []).filter(
          (x) => x.status === "active" && x.platform === "twitter"
        )
      );
      setConfigs(c.configs ?? []);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function togglePause(cfg: Config) {
    try {
      await api.patch(`/api/auto-retweet/${cfg.id}`, {
        status: cfg.status === "active" ? "paused" : "active",
      });
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove(cfg: Config) {
    const ok = await confirm({
      title: "Delete auto-retweet config",
      message: `"${cfg.name}" will stop running. Past run history is kept.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/auto-retweet/${cfg.id}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function runNow(cfg: Config) {
    const ok = await confirm({
      title: "Run auto-retweet now",
      message: `Run "${cfg.name}" immediately. This will retweet one matching tweet right away.`,
      confirmLabel: "Run now",
    });
    if (!ok) return;
    try {
      const res = await api.post<{ retweet_external_post_id?: string; error?: string; message?: string }>(
        `/api/auto-retweet/${cfg.id}/run-now`
      );
      if (res.retweet_external_post_id) {
        toast.success(`Retweeted! ID: ${res.retweet_external_post_id}`);
      } else {
        toast.success("Run completed.");
      }
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="page-title">Auto-retweet</h1>
          <p className="page-subtitle">
            Recycle your past top tweets at zero AI cost. The cron picks one match
            per cadence (default weekly) — newest first if multiple tweets pass the
            like threshold. Currently X / Twitter only.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-brand"
          disabled={accounts.length === 0}
          title={
            accounts.length === 0
              ? "Connect an X account first in Settings"
              : "Create a new auto-retweet config"
          }
        >
          + New config
        </button>
      </header>

      {loading ? (
        <div className="text-sm text-ink-500">Loading…</div>
      ) : accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-200 bg-white p-8 text-center text-sm text-ink-500">
          Connect an X / Twitter account in Settings first.
        </div>
      ) : configs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-200 bg-white p-8 text-center text-sm text-ink-500">
          No configs yet. Click <b>+ New config</b> to start recycling your top tweets.
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((cfg) => (
            <ConfigCard
              key={cfg.id}
              cfg={cfg}
              onEdit={() => setEditing(cfg)}
              onTogglePause={() => togglePause(cfg)}
              onDelete={() => remove(cfg)}
              onShowRuns={() => setShowRunsFor(cfg)}
              onPreview={() => setShowPreviewFor(cfg)}
              onRunNow={() => runNow(cfg)}
            />
          ))}
        </div>
      )}

      {showCreate ? (
        <ConfigModal
          accounts={accounts}
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            await load();
          }}
        />
      ) : null}
      {editing ? (
        <ConfigModal
          accounts={accounts}
          mode="edit"
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}
      {showRunsFor ? (
        <RunsModal
          cfg={showRunsFor}
          onClose={() => setShowRunsFor(null)}
        />
      ) : null}
      {showPreviewFor ? (
        <PreviewModal
          cfg={showPreviewFor}
          onClose={() => setShowPreviewFor(null)}
        />
      ) : null}
    </div>
  );
}

function ConfigCard(props: {
  cfg: Config;
  onEdit: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
  onShowRuns: () => void;
  onPreview: () => void;
  onRunNow: () => void;
}) {
  const c = props.cfg;
  const statusColor =
    c.status === "active"
      ? "bg-green-100 text-green-800"
      : "bg-amber-100 text-amber-800";
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white p-4 shadow-xs transition-all hover:border-ink-200 hover:shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs ${statusColor}`}>
          {c.status}
        </span>
        <span className="font-medium text-ink-900">{c.name}</span>
        <span className="text-xs text-ink-500">
          @{c.account_handle ?? "—"}
        </span>
        <span className="text-xs text-ink-400">·</span>
        <span className="text-xs text-ink-500">
          ≥ {c.min_likes} likes · {c.min_age_days}-{c.max_age_days}d old · every{" "}
          {c.cadence_days}d
        </span>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2 text-xs text-ink-600 md:grid-cols-4">
        <Stat label="Successes" value={String(c.success_count)} />
        <Stat label="Failures" value={String(c.failed_count)} />
        <Stat
          label="Last run"
          value={c.last_run_at ? fmtDate(c.last_run_at) : "never"}
        />
        <Stat label="Next due" value={fmtDate(c.next_due_at)} />
      </div>
      {c.last_error ? (
        <div className="mb-3 rounded bg-red-50 px-2 py-1 text-xs text-red-700">
          Last error: {truncate(c.last_error, 200)}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button onClick={props.onRunNow} className="font-medium text-brand-700 hover:underline">
          Run now
        </button>
        <button onClick={props.onPreview} className="text-brand-700 hover:underline">
          Preview next
        </button>
        <button onClick={props.onShowRuns} className="text-brand-700 hover:underline">
          Run history
        </button>
        <button onClick={props.onEdit} className="text-ink-700 hover:underline">
          Edit
        </button>
        <button onClick={props.onTogglePause} className="text-ink-700 hover:underline">
          {c.status === "active" ? "Pause" : "Resume"}
        </button>
        <button onClick={props.onDelete} className="text-red-600 hover:underline">
          Delete
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-400">
        {label}
      </div>
      <div className="text-ink-800">{value}</div>
    </div>
  );
}

function ConfigModal(props: {
  accounts: SocialAccountLite[];
  mode: "create" | "edit";
  existing?: Config;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [name, setName] = useState(props.existing?.name ?? "Recycle top tweets");
  const [accountId, setAccountId] = useState(
    props.existing?.social_account_id ?? props.accounts[0]?.id ?? ""
  );
  const [minLikes, setMinLikes] = useState(props.existing?.min_likes ?? 10);
  const [minAgeDays, setMinAgeDays] = useState(props.existing?.min_age_days ?? 14);
  const [maxAgeDays, setMaxAgeDays] = useState(props.existing?.max_age_days ?? 365);
  const [cadenceDays, setCadenceDays] = useState(props.existing?.cadence_days ?? 7);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (props.mode === "create") {
        await api.post("/api/auto-retweet", {
          name: name.trim(),
          social_account_id: accountId,
          min_likes: minLikes,
          min_age_days: minAgeDays,
          max_age_days: maxAgeDays,
          cadence_days: cadenceDays,
        });
      } else if (props.existing) {
        await api.patch(`/api/auto-retweet/${props.existing.id}`, {
          name: name.trim(),
          min_likes: minLikes,
          min_age_days: minAgeDays,
          max_age_days: maxAgeDays,
          cadence_days: cadenceDays,
        });
      }
      await props.onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={props.mode === "create" ? "New auto-retweet config" : "Edit config"}
      onClose={props.onClose}
    >
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="mb-1 block text-ink-600">Name</label>
          <input
            className="input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
        </div>
        <div>
          <label className="mb-1 block text-ink-600">X / Twitter account</label>
          <select
            className="input w-full"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={props.mode === "edit"}
          >
            {props.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                @{a.handle}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-ink-600">Min likes</label>
            <input
              type="number"
              min={0}
              className="input w-full"
              value={minLikes}
              onChange={(e) => setMinLikes(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div>
            <label className="mb-1 block text-ink-600">Cadence (days)</label>
            <input
              type="number"
              min={1}
              max={90}
              className="input w-full"
              value={cadenceDays}
              onChange={(e) => setCadenceDays(parseInt(e.target.value, 10) || 7)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-ink-600">Min age (days)</label>
            <input
              type="number"
              min={0}
              className="input w-full"
              value={minAgeDays}
              onChange={(e) => setMinAgeDays(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div>
            <label className="mb-1 block text-ink-600">Max age (days)</label>
            <input
              type="number"
              min={1}
              className="input w-full"
              value={maxAgeDays}
              onChange={(e) => setMaxAgeDays(parseInt(e.target.value, 10) || 365)}
            />
          </div>
        </div>
        <div className="rounded bg-ink-50 px-3 py-2 text-xs text-ink-600">
          The cron picks any tweet at @{props.accounts.find((a) => a.id === accountId)?.handle ?? "—"} that
          is between <b>{minAgeDays}</b> and <b>{maxAgeDays}</b> days old, has at least{" "}
          <b>{minLikes}</b> likes (latest analytics snapshot), and hasn't been
          retweeted by this config before. It runs every <b>{cadenceDays}</b> days at
          10:00 UTC.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={props.onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !name.trim() || !accountId}
            className="btn-primary"
          >
            {busy
              ? "Saving…"
              : props.mode === "create"
                ? "Create config"
                : "Save changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RunsModal(props: { cfg: Config; onClose: () => void }) {
  const toast = useToast();
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ runs: RunRow[] }>(`/api/auto-retweet/${props.cfg.id}/runs`)
      .then((r) => setRuns(r.runs ?? []))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [props.cfg.id, toast]);

  return (
    <Modal title={`Run history — ${props.cfg.name}`} onClose={props.onClose}>
      <div className="space-y-2 p-4 text-sm">
        {loading ? (
          <div className="text-ink-500">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="text-ink-500">No runs yet.</div>
        ) : (
          runs.map((r) => (
            <div
              key={r.id}
              className="rounded border border-ink-200 bg-white p-3 shadow-sm"
            >
              <div className="mb-1 flex items-center gap-2 text-xs">
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    r.status === "success"
                      ? "bg-green-100 text-green-800"
                      : r.status === "failed"
                        ? "bg-red-100 text-red-700"
                        : "bg-ink-100 text-ink-700"
                  }`}
                >
                  {r.status}
                </span>
                <span className="text-ink-500">{fmtDate(r.created_at)}</span>
                {r.source_likes_snapshot != null ? (
                  <span className="text-ink-500">
                    · {r.source_likes_snapshot} likes
                  </span>
                ) : null}
              </div>
              {r.source_content_preview ? (
                <p className="whitespace-pre-wrap text-ink-800">
                  {truncate(r.source_content_preview, 240)}
                </p>
              ) : null}
              {r.error_message ? (
                <div className="mt-1 rounded bg-red-50 px-2 py-1 text-xs text-red-700">
                  {truncate(r.error_message, 240)}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

function PreviewModal(props: { cfg: Config; onClose: () => void }) {
  const toast = useToast();
  const [candidate, setCandidate] = useState<PreviewCandidate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ candidate: PreviewCandidate | null }>(
        `/api/auto-retweet/${props.cfg.id}/preview`
      )
      .then((r) => setCandidate(r.candidate ?? null))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [props.cfg.id, toast]);

  return (
    <Modal title="Preview next retweet" onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        {loading ? (
          <div className="text-ink-500">Loading…</div>
        ) : !candidate ? (
          <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
            No tweet matches the current rules right now. Either wait for new posts to
            age past min_age_days, or lower the like threshold.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span>{fmtRelative(candidate.published_at)}</span>
              <span>·</span>
              <span className="font-medium text-ink-700">
                {candidate.likes} likes
              </span>
            </div>
            <p className="whitespace-pre-wrap rounded bg-ink-50 px-3 py-2 text-ink-800">
              {candidate.content}
            </p>
            {candidate.external_post_url ? (
              <a
                href={candidate.external_post_url}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-xs text-brand-600 hover:underline"
              >
                View original →
              </a>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Modal(props: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in"
      onClick={props.onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-ink-200/60 bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <h3 className="text-base font-semibold text-ink-900">{props.title}</h3>
          <button
            onClick={props.onClose}
            className="text-ink-500 hover:text-ink-900"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}
